import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Copy, CheckCircle, Clock, AlertCircle, Edit2, Save, X, Star, Upload, Trash2, Image, ExternalLink, Plus, MousePointer, Gift, DollarSign, Eye, EyeOff, Rocket, PenLine, Briefcase, Sparkles, BarChart2, TrendingUp, RefreshCw, ChevronDown, ChevronUp, Wand2, Lightbulb } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { INDUSTRY_TYPES, getIndustryType } from "@/constants/industries";
import RotatingBanners from "@/components/RotatingBanners";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusBadge = (s) => {
  const styles = {
    paid: "bg-[#2ECC71]/10 text-[#27AE60] border-[#2ECC71]/20",
    active: "bg-[#2ECC71]/10 text-[#27AE60] border-[#2ECC71]/20",
    pending: "bg-[#FFD700]/20 text-[#856404] border-[#FFD700]/40",
    expired: "bg-slate-100 text-[#64748B] border-slate-200",
    cancelled: "bg-red-50 text-red-600 border-red-200",
  };
  return `inline-flex items-center border rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[s] || styles.pending}`;
};

export default function PartnerPortal() {
  const { user } = useAuth();
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [leads, setLeads] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [profile, setProfile] = useState({});
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [refLinkCopied, setRefLinkCopied] = useState(false);

  // Banner ads state
  const [myBanners, setMyBanners] = useState([]);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({ claim_id: "", title: "", tagline: "", cta_text: "Learn More", cta_url: "", image_url: "" });
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [applyLogoUrl, setApplyLogoUrl] = useState("");
  const [applyLogoMsg, setApplyLogoMsg] = useState("");
  const fileInputRef = useRef(null);

  // AI Ad Helper state
  const [aiPanel, setAiPanel] = useState(null); // { mode: "generate"|"improve", banner?: obj }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");
  const [aiGenForm, setAiGenForm] = useState({ key_message: "", tone: "professional" });
  // Expanded stats per banner
  const [expandedStats, setExpandedStats] = useState({});

  useEffect(() => {
    setProfile(user || {});
    setEditForm(user || {});
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [claimsRes, leadsRes, bannersRes, refRes, jobsRes] = await Promise.all([
        axios.get(`${API}/providers/my-claims`, { headers: authHeaders() }),
        axios.get(`${API}/leads/my-leads`, { headers: authHeaders() }),
        axios.get(`${API}/marketplace/my-banners`, { headers: authHeaders() }),
        axios.get(`${API}/referral/stats`, { headers: authHeaders() }),
        axios.get(`${API}/jobs/my/posts`, { headers: authHeaders() }),
      ]);
      setClaims(claimsRes.data.claims || []);
      setLeads(leadsRes.data.leads || []);
      setMyBanners(bannersRes.data.banners || []);
      setReferralStats(refRes.data);
      setMyJobs(jobsRes.data.jobs || []);
    } catch {}
    setLoading(false);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await axios.put(`${API}/providers/profile`, editForm, { headers: authHeaders() });
      setProfile(res.data);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
  };

  // ── Banner helpers ──────────────────────────────────────────────────────────
  const openCreateBanner = () => {
    setEditingBanner(null);
    setBannerForm({ claim_id: activeClaims[0]?.id || "", title: "", tagline: "", cta_text: "Learn More", cta_url: "", image_url: "" });
    setBannerError("");
    setShowBannerForm(true);
  };

  const openEditBanner = (b) => {
    setEditingBanner(b);
    setBannerForm({ claim_id: b.claim_id, title: b.title, tagline: b.tagline || "", cta_text: b.cta_text || "Learn More", cta_url: b.cta_url || "", image_url: b.image_url?.startsWith("http") ? b.image_url : "" });
    setBannerError("");
    setShowBannerForm(true);
  };

  const handleSaveBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.title.trim()) { setBannerError("Title is required."); return; }
    setBannerSaving(true);
    setBannerError("");
    try {
      if (editingBanner) {
        const res = await axios.put(`${API}/marketplace/banners/${editingBanner.id}`, bannerForm, { headers: authHeaders() });
        setMyBanners(bs => bs.map(b => b.id === editingBanner.id ? res.data : b));
      } else {
        const res = await axios.post(`${API}/marketplace/banners`, bannerForm, { headers: authHeaders() });
        setMyBanners(bs => [res.data, ...bs]);
      }
      setShowBannerForm(false);
    } catch (err) {
      setBannerError(err?.response?.data?.detail || "Failed to save banner.");
    }
    setBannerSaving(false);
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm("Delete this banner ad?")) return;
    try {
      await axios.delete(`${API}/marketplace/banners/${bannerId}`, { headers: authHeaders() });
      setMyBanners(bs => bs.filter(b => b.id !== bannerId));
    } catch {}
  };

  const handleImageUpload = async (bannerId, file) => {
    if (!file) return;
    setUploadingImageFor(bannerId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/marketplace/banners/${bannerId}/upload-image`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setMyBanners(bs => bs.map(b => b.id === bannerId ? { ...b, image_url: res.data.image_url } : b));
    } catch (err) {
      alert(err?.response?.data?.detail || "Image upload failed.");
    }
    setUploadingImageFor(null);
  };

  const handleApplyLogo = async () => {
    if (!applyLogoUrl.trim()) return;
    setApplyLogoMsg("");
    try {
      const res = await axios.put(`${API}/marketplace/banners/apply-logo`, { image_url: applyLogoUrl.trim() }, { headers: authHeaders() });
      setApplyLogoMsg(res.data.message || "Logo applied!");
      setMyBanners(bs => bs.map(b => ({ ...b, image_url: applyLogoUrl.trim() })));
    } catch (err) {
      setApplyLogoMsg(err?.response?.data?.detail || "Failed to apply logo.");
    }
  };

  // AI helper: generate ad content
  const handleAIGenerate = async (banner) => {
    if (!banner) return;
    setAiLoading(true);
    setAiResult(null);
    setAiError("");
    try {
      const claim = activeClaims.find(c => c.id === banner.claim_id);
      const res = await axios.post(`${API}/ai/generate-ad`, {
        industry: banner.advertiser_type || claim?.advertiser_type || "tax_pro",
        zip_code: banner.zip_code || claim?.zip_code || "",
        city: banner.city || claim?.city || "",
        state: banner.state || claim?.state || "",
        business_name: banner.business_name || claim?.business_name || "",
        key_message: aiGenForm.key_message,
        tone: aiGenForm.tone,
      }, { headers: authHeaders() });
      setAiResult({ type: "generate", data: res.data });
    } catch (err) {
      setAiError(err?.response?.data?.detail || "AI generation failed. Please try again.");
    }
    setAiLoading(false);
  };

  // AI helper: analyze & improve existing ad
  const handleAIImprove = async (banner) => {
    setAiLoading(true);
    setAiResult(null);
    setAiError("");
    try {
      const res = await axios.post(`${API}/ai/improve-ad`, {
        title: banner.title,
        tagline: banner.tagline || "",
        cta_text: banner.cta_text || "Learn More",
        industry: banner.advertiser_type || "tax_pro",
        zip_code: banner.zip_code || "",
        city: banner.city || "",
        state: banner.state || "",
        business_name: banner.business_name || "",
      }, { headers: authHeaders() });
      setAiResult({ type: "improve", data: res.data });
    } catch (err) {
      setAiError(err?.response?.data?.detail || "AI analysis failed. Please try again.");
    }
    setAiLoading(false);
  };

  // Apply AI suggestions to banner form
  const applyAISuggestion = (field, value) => {
    setBannerForm(f => ({ ...f, [field]: value }));
  };

  const handlePublish = async (bannerId) => {
    try {
      await axios.post(`${API}/marketplace/banners/${bannerId}/publish`, {}, { headers: authHeaders() });
      setMyBanners(bs => bs.map(b => b.id === bannerId ? { ...b, is_active: true } : b));
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not publish ad.");
    }
  };

  const handleUnpublish = async (bannerId) => {
    try {
      await axios.post(`${API}/marketplace/banners/${bannerId}/unpublish`, {}, { headers: authHeaders() });
      setMyBanners(bs => bs.map(b => b.id === bannerId ? { ...b, is_active: false } : b));
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not take ad offline.");
    }
  };

  const badgeHtml = `<a href="https://nextdayrefund.com" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none;"><div style="background:#003366;color:#fff;border-radius:8px;padding:12px 18px;display:inline-flex;align-items:center;gap:10px;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.15);"><span style="background:#2ECC71;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;flex-shrink:0;">&#10003;</span><div><div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;opacity:0.7;margin-bottom:2px;">Verified Provider</div><div style="font-weight:bold;font-size:13px;">NextDayRefund.com</div></div></div></a>`;

  const copyBadge = () => {
    navigator.clipboard.writeText(badgeHtml).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const activeClaims = claims.filter(c => ["paid", "active"].includes(c.payment_status));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Header */}
      <div className="bg-[#003366] py-8">
        <div className="section-container">
          <h1 className="text-2xl font-black text-white">Partner Portal</h1>
          <p className="text-slate-300 text-sm mt-1">Welcome back, {profile.name || profile.email}</p>
          <div className="flex gap-4 mt-4 flex-wrap">
            <div className="text-center">
              <div className="text-xl font-black text-[#2ECC71]">{activeClaims.length}</div>
              <div className="text-xs text-slate-300">Active ZIPs</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-black text-[#2ECC71]">{leads.length}</div>
              <div className="text-xs text-slate-300">Total Leads</div>
            </div>
            {referralStats?.credit_balance > 0 && (
              <div className="text-center">
                <div className="text-xl font-black text-[#FFD700]">${referralStats.credit_balance.toFixed(0)}</div>
                <div className="text-xs text-slate-300">Referral Credit</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section-container py-8">
        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit mb-6 flex-wrap" data-testid="portal-tabs">
          {["claims", "leads", "ads", "jobs", "referral", "profile", "badge"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all capitalize ${tab === t ? "bg-[#003366] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}>
              {t === "claims" ? "My ZIP Codes" : t === "badge" ? "My Badge" : t === "ads" ? "My Ads" : t === "referral" ? "Refer & Earn" : t === "jobs" ? "My Jobs" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {loading && <div className="text-center py-8 text-[#64748B]">Loading...</div>}

        {/* Claims Tab */}
        {!loading && tab === "claims" && (
          <div data-testid="claims-tab">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Your ZIP Code Claims</h2>
              <Link to="/claim" className="btn-action !py-2 !px-4 !text-sm" data-testid="add-zip-btn">+ Claim New ZIP</Link>
            </div>
            {claims.length === 0 ? (
              <div className="card-base text-center py-10" data-testid="no-claims">
                <MapPin size={40} className="text-[#003366] mx-auto mb-3" />
                <p className="font-semibold text-[#0F172A] mb-2">No ZIP codes claimed yet</p>
                <Link to="/claim" className="btn-action inline-block" data-testid="first-claim-btn">Claim Your First ZIP Code</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {claims.map(c => (
                  <div key={c.id} className="card-base" data-testid={`claim-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-xl font-black text-[#003366]">{c.zip_code}</span>
                          <span className={statusBadge(c.payment_status)}>{c.payment_status}</span>
                        </div>
                        <div className="text-sm text-[#64748B]">{c.business_name}</div>
                        <div className="text-xs text-[#64748B] mt-1">{c.city}, {c.state} · Expires {c.expires_at?.split("T")[0] || "Apr 15, 2027"}</div>
                      </div>
                      {c.payment_status === "pending" && (
                        <span className="text-xs text-[#856404] bg-[#FFD700]/20 px-2 py-1 rounded flex items-center gap-1">
                          <Clock size={12} /> Awaiting payment
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leads Tab */}
        {!loading && tab === "leads" && (
          <div data-testid="leads-tab">
            <h2 className="text-lg font-bold text-[#0F172A] mb-4">Leads from Your ZIP Codes</h2>
            {leads.length === 0 ? (
              <div className="card-base text-center py-10" data-testid="no-leads">
                <Users size={40} className="text-[#003366] mx-auto mb-3" />
                <p className="font-semibold text-[#0F172A] mb-1">No leads yet</p>
                <p className="text-sm text-[#64748B]">Taxpayers who contact you through NextDayRefund.com will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map(l => (
                  <div key={l.id} className="card-base" data-testid={`lead-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-semibold text-[#0F172A]">{l.taxpayer_name || "Anonymous"}</div>
                        <div className="text-sm text-[#64748B]">ZIP: {l.zip_code}</div>
                        {l.taxpayer_phone && <div className="text-sm text-[#003366]">{l.taxpayer_phone}</div>}
                        {l.taxpayer_email && <div className="text-sm text-[#003366]">{l.taxpayer_email}</div>}
                        {l.message && <div className="text-sm text-[#334155] mt-2 italic">"{l.message}"</div>}
                      </div>
                      <div className="text-xs text-[#64748B]">{new Date(l.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {!loading && tab === "profile" && (
          <div data-testid="profile-tab">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Your Profile</h2>
              {!editing ? (
                <button onClick={() => setEditing(true)} className="btn-outline !py-2 !px-4 !text-sm flex items-center gap-1.5" data-testid="edit-profile-btn">
                  <Edit2 size={14} /> Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={handleSaveProfile} className="btn-primary !py-2 !px-4 !text-sm flex items-center gap-1.5" data-testid="save-profile-btn">
                    <Save size={14} /> Save
                  </button>
                  <button onClick={() => setEditing(false)} className="btn-outline !py-2 !px-3 !text-sm" data-testid="cancel-edit-btn"><X size={14} /></button>
                </div>
              )}
            </div>
            {saved && <div className="flex items-center gap-2 text-[#27AE60] font-semibold text-sm mb-4" data-testid="profile-saved"><CheckCircle size={16} /> Profile saved!</div>}
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
              {["name", "business_name", "phone", "city", "state", "address", "website", "description"].map(field => (
                <div key={field}>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1 capitalize">{field.replace("_", " ")}</label>
                  {editing ? (
                    <input className="input-field" value={editForm[field] || ""} onChange={e => setEditForm({...editForm, [field]: e.target.value})} data-testid={`profile-${field}-input`} />
                  ) : (
                    <div className="text-[#334155] text-sm py-2.5">{profile[field] || <span className="text-[#94A3B8] italic">Not set</span>}</div>
                  )}
                </div>
              ))}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1">Email</label>
                <div className="text-[#334155] text-sm py-2.5">{profile.email}</div>
              </div>
            </div>
          </div>
        )}

        {/* My Ads Tab */}
        {!loading && tab === "ads" && (
          <div data-testid="ads-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">My Banner Ads</h2>
                <p className="text-sm text-[#64748B] mt-0.5">Create and manage banner ads for your claimed ZIP codes.</p>
              </div>
              {activeClaims.length > 0 && (
                <button onClick={openCreateBanner} className="btn-action !py-2 !px-4 !text-sm flex items-center gap-1.5" data-testid="create-banner-btn">
                  <Plus size={14} /> Create Banner
                </button>
              )}
            </div>

            {activeClaims.length === 0 && (
              <div className="card-base text-center py-10" data-testid="no-active-claims-ads">
                <Star size={40} className="text-[#003366] mx-auto mb-3" />
                <p className="font-semibold text-[#0F172A] mb-2">No active ZIP claims yet</p>
                <p className="text-sm text-[#64748B] mb-4">Claim and pay for a ZIP code first, then create your banner ad.</p>
                <Link to="/claim" className="btn-action inline-block">Claim a ZIP Code</Link>
              </div>
            )}

            {/* Banner Create / Edit Form — 2-column with live preview */}
            {showBannerForm && (() => {
              const selectedClaim = activeClaims.find(c => c.id === bannerForm.claim_id) || activeClaims[0];
              const previewBanner = [{
                id: "preview",
                title: bannerForm.title || "Your Ad Title",
                tagline: bannerForm.tagline || "Your tagline will appear here…",
                cta_text: bannerForm.cta_text || "Learn More",
                cta_url: bannerForm.cta_url || "#",
                image_url: bannerForm.image_url || "",
                zip_code: selectedClaim?.zip_code || "00000",
                advertiser_type: selectedClaim?.advertiser_type || "tax_pro",
                business_name: selectedClaim?.business_name || "Your Business",
              }];
              return (
                <div className="rounded-2xl border-2 border-[#003366]/20 bg-white overflow-hidden mb-6" data-testid="banner-form">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <PenLine size={17} className="text-[#003366]" />
                      <h3 className="font-bold text-[#0F172A]">{editingBanner ? "Edit Banner Ad" : "Create New Banner Ad"}</h3>
                    </div>
                    <button onClick={() => setShowBannerForm(false)} className="text-[#64748B] hover:text-[#0F172A]"><X size={18} /></button>
                  </div>

                  {/* Draft notice for new banners */}
                  {!editingBanner && (
                    <div className="flex items-start gap-3 bg-amber-50 border-b border-amber-100 px-6 py-3">
                      <Eye size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-800">
                        <strong>Your ad starts as a Draft</strong> — visitors won't see it until you click <strong>"Publish Ad"</strong> in your ad list. Use the live preview on the right to perfect it first.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    {/* Left — Form */}
                    <div className="p-6 border-r border-slate-100">
                      <form onSubmit={handleSaveBanner} className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">ZIP Code Claim *</label>
                          <select
                            value={bannerForm.claim_id}
                            onChange={e => setBannerForm({ ...bannerForm, claim_id: e.target.value })}
                            className="input-field" required data-testid="banner-claim-select"
                          >
                            <option value="">Select a ZIP code claim...</option>
                            {activeClaims.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.zip_code} — {c.business_name} ({getIndustryType(c.advertiser_type)?.label || "Tax Professional"})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Ad Title *</label>
                          <div className="flex gap-2">
                            <input type="text" required value={bannerForm.title}
                              onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })}
                              className="input-field flex-1" placeholder="Best Tax Prep in Downtown Denver"
                              data-testid="banner-title-input" />
                            <button type="button"
                              onClick={() => { setAiPanel({ mode: "generate", banner: editingBanner || { advertiser_type: activeClaims.find(c=>c.id===bannerForm.claim_id)?.advertiser_type, zip_code: activeClaims.find(c=>c.id===bannerForm.claim_id)?.zip_code, city: activeClaims.find(c=>c.id===bannerForm.claim_id)?.city, state: activeClaims.find(c=>c.id===bannerForm.claim_id)?.state, business_name: activeClaims.find(c=>c.id===bannerForm.claim_id)?.business_name, claim_id: bannerForm.claim_id } }); setAiResult(null); setAiError(""); }}
                              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border transition-colors"
                              style={{ borderColor: "#8B5CF6", color: "#8B5CF6", background: "rgba(139,92,246,0.08)" }}
                              title="Generate with AI"
                              data-testid="ai-generate-btn">
                              <Wand2 size={13} /> AI
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Tagline</label>
                          <input type="text" value={bannerForm.tagline}
                            onChange={e => setBannerForm({ ...bannerForm, tagline: e.target.value })}
                            className="input-field" placeholder="Get your refund advance in 24 hours"
                            data-testid="banner-tagline-input" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Button Text</label>
                            <input type="text" value={bannerForm.cta_text}
                              onChange={e => setBannerForm({ ...bannerForm, cta_text: e.target.value })}
                              className="input-field" placeholder="Get a Free Quote"
                              data-testid="banner-cta-text-input" />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Button URL</label>
                            <input type="url" value={bannerForm.cta_url}
                              onChange={e => setBannerForm({ ...bannerForm, cta_url: e.target.value })}
                              className="input-field" placeholder="https://yourbusiness.com"
                              data-testid="banner-cta-url-input" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                            Logo / Image URL <span className="text-[#64748B] font-normal text-xs">(optional)</span>
                          </label>
                          <input type="url" value={bannerForm.image_url}
                            onChange={e => setBannerForm({ ...bannerForm, image_url: e.target.value })}
                            className="input-field" placeholder="https://yourdomain.com/logo.png"
                            data-testid="banner-image-url-input" />
                          <p className="text-xs text-[#64748B] mt-1">Paste a URL from your website or CDN — loads on demand, no server storage used.</p>
                        </div>
                        {bannerError && <p className="text-red-500 text-sm" data-testid="banner-error">{bannerError}</p>}
                        <div className="flex gap-3 pt-1">
                          <button type="submit" disabled={bannerSaving} className="btn-action !py-2 !px-5 !text-sm" data-testid="banner-save-btn">
                            {bannerSaving ? "Saving..." : editingBanner ? "Save Changes" : "Save as Draft"}
                          </button>
                          <button type="button" onClick={() => setShowBannerForm(false)} className="btn-outline !py-2 !px-4 !text-sm">Cancel</button>
                        </div>
                      </form>
                    </div>

                    {/* Right — Live Preview */}
                    <div className="p-6 bg-slate-900/3">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-1.5">
                        <Eye size={12} /> Live Preview
                      </p>
                      <RotatingBanners banners={previewBanner} />
                      <p className="text-xs text-[#64748B] text-center mt-3">
                        This is exactly how your ad appears to visitors
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Apply Logo to All Banners */}
            {myBanners.length > 1 && (
              <div className="bg-[#F0F9FF] border border-[#003366]/20 rounded-xl p-4 mb-5" data-testid="apply-logo-section">
                <h4 className="font-bold text-[#003366] text-sm mb-1">Apply One Logo/Image to All Your Ads</h4>
                <p className="text-xs text-[#64748B] mb-3">Paste a URL to your logo or image (hosted on your site or CDN) — it will be applied to every one of your {myBanners.length} banner ads at once.</p>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="url"
                    value={applyLogoUrl}
                    onChange={e => setApplyLogoUrl(e.target.value)}
                    placeholder="https://yourdomain.com/logo.png"
                    className="input-field flex-1 min-w-[200px] !py-2 !text-sm"
                    data-testid="apply-logo-url-input"
                  />
                  <button
                    onClick={handleApplyLogo}
                    disabled={!applyLogoUrl.trim()}
                    className="btn-action !py-2 !px-4 !text-sm disabled:opacity-50"
                    data-testid="apply-logo-btn"
                  >
                    Apply to All {myBanners.length} Ads
                  </button>
                </div>
                {applyLogoMsg && (
                  <p className="text-xs font-semibold text-[#27AE60] mt-2" data-testid="apply-logo-msg">{applyLogoMsg}</p>
                )}
              </div>
            )}

            {/* Existing Banners */}
            {myBanners.length > 0 && (
              <div className="space-y-4">
                {myBanners.map(b => {
                  const ind = getIndustryType(b.advertiser_type || "tax_pro");
                  const IndIcon = ind.icon;
                  const imgSrc = b.image_url
                    ? (b.image_url.startsWith("http") ? b.image_url : `${process.env.REACT_APP_BACKEND_URL}${b.image_url}`)
                    : null;
                  const clicks = b.click_count || 0;
                  const impressions = b.impression_count || 0;
                  const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(1) : "0.0";
                  const statsOpen = expandedStats[b.id];
                  return (
                    <div key={b.id} className="card-base" data-testid={`banner-card-${b.id}`}>
                      <div className="flex gap-4 flex-col sm:flex-row">
                        {/* Image preview */}
                        <div className="flex-shrink-0 w-full sm:w-36 h-28 rounded-lg overflow-hidden bg-[#F8FAFC] border border-slate-200 flex items-center justify-center relative group">
                          {imgSrc ? (
                            <img src={imgSrc} alt={b.title} className="w-full h-full object-cover" />
                          ) : (
                            <Image size={32} className="text-slate-300" />
                          )}
                          <label className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white text-xs font-semibold gap-1">
                            <Upload size={16} />
                            {uploadingImageFor === b.id ? "Uploading..." : "Upload Image"}
                            <input
                              type="file" accept="image/*" className="hidden"
                              disabled={uploadingImageFor === b.id}
                              onChange={e => e.target.files[0] && handleImageUpload(b.id, e.target.files[0])}
                              data-testid={`image-upload-${b.id}`}
                            />
                          </label>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full bg-[#003366]/10 text-[#003366]">
                                  <IndIcon size={11} /> {ind.label}
                                </span>
                                <span className="text-xs text-[#64748B]">ZIP {b.zip_code} · {b.city}, {b.state}</span>
                                {b.is_active ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#27AE60] bg-[#2ECC71]/10 px-2 py-0.5 rounded-full">
                                    <Eye size={10} /> Live
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                    <EyeOff size={10} /> Draft
                                  </span>
                                )}
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#003366] bg-[#003366]/10 px-2 py-0.5 rounded-full" data-testid={`click-count-${b.id}`}>
                                  <MousePointer size={10} /> {clicks} clicks
                                </span>
                              </div>
                              <h3 className="font-bold text-[#0F172A]">{b.title}</h3>
                              {b.tagline && <p className="text-sm text-[#64748B] mt-0.5">{b.tagline}</p>}
                              {b.cta_url && (
                                <a href={b.cta_url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-[#003366] mt-1 hover:underline">
                                  {b.cta_text || "CTA"} <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {b.is_active ? (
                                <button onClick={() => handleUnpublish(b.id)}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-[#64748B] hover:border-amber-300 hover:text-amber-700 transition-colors"
                                  data-testid={`unpublish-banner-${b.id}`}>
                                  <EyeOff size={12} /> Take Offline
                                </button>
                              ) : (
                                <button onClick={() => handlePublish(b.id)}
                                  className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-1.5 rounded-lg bg-[#2ECC71] hover:bg-[#27AE60] text-white transition-colors"
                                  data-testid={`publish-banner-${b.id}`}>
                                  <Rocket size={12} /> Publish Ad
                                </button>
                              )}
                              {/* AI Suggestions button */}
                              <button
                                onClick={() => { setAiPanel({ mode: "improve", banner: b }); setAiResult(null); setAiError(""); }}
                                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors"
                                style={{ borderColor: "#8B5CF6", color: "#8B5CF6", background: "rgba(139,92,246,0.06)" }}
                                data-testid={`ai-improve-btn-${b.id}`}
                                title="Get AI improvement suggestions">
                                <Sparkles size={11} /> AI Tips
                              </button>
                              {/* Stats toggle */}
                              <button
                                onClick={() => setExpandedStats(s => ({ ...s, [b.id]: !s[b.id] }))}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-[#64748B] hover:border-[#003366] hover:text-[#003366] transition-colors"
                                data-testid={`stats-toggle-${b.id}`}>
                                <BarChart2 size={11} /> Stats {statsOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                              </button>
                              <button onClick={() => openEditBanner(b)} className="p-1.5 rounded-lg border border-slate-200 hover:border-[#003366] hover:text-[#003366] transition-colors" data-testid={`edit-banner-${b.id}`}>
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => handleDeleteBanner(b.id)} className="p-1.5 rounded-lg border border-slate-200 hover:border-red-300 hover:text-red-500 transition-colors" data-testid={`delete-banner-${b.id}`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Performance Stats Panel */}
                          {statsOpen && (
                            <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`stats-panel-${b.id}`}>
                              <p className="text-xs font-bold text-[#64748B] uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                <TrendingUp size={10} /> Ad Performance
                              </p>
                              <div className="grid grid-cols-3 gap-3">
                                <div className="bg-[#F0F9FF] rounded-lg p-3 text-center border border-[#003366]/10">
                                  <div className="text-xl font-black text-[#003366]">{impressions.toLocaleString()}</div>
                                  <div className="text-xs text-[#64748B] mt-0.5">Impressions</div>
                                </div>
                                <div className="bg-[#F0FDF4] rounded-lg p-3 text-center border border-[#2ECC71]/20">
                                  <div className="text-xl font-black text-[#27AE60]">{clicks.toLocaleString()}</div>
                                  <div className="text-xs text-[#64748B] mt-0.5">Clicks</div>
                                </div>
                                <div className="bg-[#FFF8E1] rounded-lg p-3 text-center border border-[#FFD700]/30">
                                  <div className="text-xl font-black text-[#856404]">{ctr}%</div>
                                  <div className="text-xs text-[#64748B] mt-0.5">CTR</div>
                                </div>
                              </div>
                              {impressions === 0 && (
                                <p className="text-xs text-[#64748B] mt-2">
                                  Impressions are tracked when your ad is shown live in the marketplace. Publish your ad to start collecting data.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeClaims.length > 0 && myBanners.length === 0 && !showBannerForm && (
              <div className="card-base text-center py-10" data-testid="no-banners">
                <Image size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="font-semibold text-[#0F172A] mb-1">No banner ads yet</p>
                <p className="text-sm text-[#64748B] mb-4">Create a banner ad to be displayed in the Marketplace — visible to thousands of visitors.</p>
                <button onClick={openCreateBanner} className="btn-action inline-flex items-center gap-2">
                  <Plus size={16} /> Create Your First Banner
                </button>
              </div>
            )}
          </div>
        )}

        {/* =================== AI Ad Helper Panel =================== */}
        {aiPanel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(2,6,15,0.85)", backdropFilter: "blur(6px)" }}
            data-testid="ai-panel">
            <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: "#0d1829", border: "1px solid rgba(139,92,246,0.3)" }}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4"
                style={{ borderBottom: "1px solid rgba(139,92,246,0.15)", background: "rgba(139,92,246,0.06)" }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}>
                    {aiPanel.mode === "generate" ? <Wand2 size={15} className="text-white" /> : <Sparkles size={15} className="text-white" />}
                  </div>
                  <div>
                    <h3 className="font-black text-white text-base">
                      {aiPanel.mode === "generate" ? "AI Ad Content Generator" : "AI Ad Improvement Analysis"}
                    </h3>
                    <p className="text-xs text-slate-400">
                      {aiPanel.mode === "generate" ? "Generate compelling ad copy for your banner" : `Analyzing: "${aiPanel.banner?.title}"`}
                    </p>
                  </div>
                </div>
                <button onClick={() => { setAiPanel(null); setAiResult(null); setAiError(""); }}
                  className="text-slate-400 hover:text-white transition-colors" data-testid="ai-panel-close">
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto" style={{ maxHeight: "70vh" }}>
                {/* Generate mode */}
                {aiPanel.mode === "generate" && !aiResult && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-300">
                      Tell the AI a bit about your message and tone. The AI knows your business type, ZIP code, and location — just tell it what you want to emphasize.
                    </p>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">What's the key message you want to convey?</label>
                      <textarea
                        value={aiGenForm.key_message}
                        onChange={e => setAiGenForm(f => ({ ...f, key_message: e.target.value }))}
                        placeholder="e.g. Fast refunds, same-day appointments, best rates in town…"
                        className="w-full rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(139,92,246,0.2)", color: "white", minHeight: "80px" }}
                        data-testid="ai-key-message-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Tone</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                          { val: "professional", label: "Professional" },
                          { val: "friendly", label: "Friendly" },
                          { val: "urgent", label: "Urgent" },
                          { val: "bold", label: "Bold" },
                        ].map(t => (
                          <button key={t.val} type="button"
                            onClick={() => setAiGenForm(f => ({ ...f, tone: t.val }))}
                            className="py-2 px-3 rounded-lg text-sm font-semibold transition-all"
                            data-testid={`tone-${t.val}`}
                            style={aiGenForm.tone === t.val
                              ? { background: "linear-gradient(135deg, #8B5CF6, #6D28D9)", color: "white", boxShadow: "0 2px 8px rgba(139,92,246,0.4)" }
                              : { background: "rgba(255,255,255,0.05)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.08)" }}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {aiError && <p className="text-rose-400 text-sm">{aiError}</p>}
                    <button
                      onClick={() => handleAIGenerate(aiPanel.banner)}
                      disabled={aiLoading}
                      className="w-full flex items-center justify-center gap-2 font-black py-3 rounded-xl transition-all disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #8B5CF6, #6D28D9)", color: "white" }}
                      data-testid="ai-generate-submit">
                      {aiLoading ? <><RefreshCw size={15} className="animate-spin" /> Generating...</> : <><Wand2 size={15} /> Generate Ad Copy</>}
                    </button>
                  </div>
                )}

                {/* Improve mode */}
                {aiPanel.mode === "improve" && !aiResult && (
                  <div className="space-y-4">
                    <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                      <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Current Ad</div>
                      <div className="text-white font-bold mb-1">{aiPanel.banner?.title}</div>
                      {aiPanel.banner?.tagline && <div className="text-slate-300 text-sm">{aiPanel.banner.tagline}</div>}
                      {aiPanel.banner?.cta_text && (
                        <div className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold"
                          style={{ background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.25)" }}>
                          {aiPanel.banner.cta_text}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-300">
                      The AI will score your current ad, identify what's working, and suggest specific improvements to each field.
                    </p>
                    {aiError && <p className="text-rose-400 text-sm">{aiError}</p>}
                    <button
                      onClick={() => handleAIImprove(aiPanel.banner)}
                      disabled={aiLoading}
                      className="w-full flex items-center justify-center gap-2 font-black py-3 rounded-xl transition-all disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #8B5CF6, #6D28D9)", color: "white" }}
                      data-testid="ai-improve-submit">
                      {aiLoading ? <><RefreshCw size={15} className="animate-spin" /> Analyzing...</> : <><Sparkles size={15} /> Analyze My Ad</>}
                    </button>
                  </div>
                )}

                {/* Results: Generated */}
                {aiResult?.type === "generate" && (
                  <div className="space-y-4" data-testid="ai-generate-result">
                    <div className="flex items-center gap-2 text-[#2ECC71] text-sm font-bold">
                      <CheckCircle size={16} /> AI-Generated Ad Copy
                    </div>
                    <div className="space-y-3">
                      {[
                        { field: "title", label: "Headline", value: aiResult.data.title, placeholder: "Ad title" },
                        { field: "tagline", label: "Tagline", value: aiResult.data.tagline, placeholder: "Tagline" },
                        { field: "cta_text", label: "CTA Button", value: aiResult.data.cta_text, placeholder: "CTA" },
                      ].map(({ field, label, value }) => (
                        <div key={field} className="rounded-xl p-4 group"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">{label}</div>
                              <div className="text-white font-semibold">{value}</div>
                            </div>
                            <button
                              onClick={() => applyAISuggestion(field, value)}
                              className="flex-shrink-0 flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                              style={{ background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.3)" }}
                              data-testid={`apply-ai-${field}`}>
                              <Plus size={10} /> Apply
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {aiResult.data.rationale && (
                      <div className="rounded-xl p-3 flex items-start gap-2"
                        style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)" }}>
                        <Lightbulb size={13} className="text-violet-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-slate-300">{aiResult.data.rationale}</p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={() => { applyAISuggestion("title", aiResult.data.title); applyAISuggestion("tagline", aiResult.data.tagline); applyAISuggestion("cta_text", aiResult.data.cta_text); setAiPanel(null); setAiResult(null); }}
                        className="flex-1 flex items-center justify-center gap-2 font-black py-2.5 rounded-xl text-sm"
                        style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", color: "white" }}
                        data-testid="apply-all-ai">
                        <CheckCircle size={14} /> Apply All to Form
                      </button>
                      <button onClick={() => setAiResult(null)}
                        className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl"
                        style={{ background: "rgba(255,255,255,0.05)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <RefreshCw size={12} /> Regenerate
                      </button>
                    </div>
                  </div>
                )}

                {/* Results: Improvement */}
                {aiResult?.type === "improve" && (
                  <div className="space-y-4" data-testid="ai-improve-result">
                    {/* Score */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center border-4 flex-shrink-0"
                        style={{
                          borderColor: aiResult.data.overall_score >= 7 ? "#2ECC71" : aiResult.data.overall_score >= 5 ? "#facc15" : "#f43f5e",
                          background: "rgba(0,0,0,0.2)"
                        }}>
                        <span className="text-2xl font-black text-white">{aiResult.data.overall_score}</span>
                      </div>
                      <div>
                        <div className="text-white font-bold text-base">Ad Quality Score</div>
                        <div className="text-slate-400 text-sm">{
                          aiResult.data.overall_score >= 8 ? "Strong ad — minor refinements only" :
                          aiResult.data.overall_score >= 6 ? "Good foundation — improvements will help" :
                          "Significant opportunity to improve"
                        }</div>
                      </div>
                    </div>

                    {/* Top tip */}
                    {aiResult.data.top_tip && (
                      <div className="rounded-xl p-4 flex items-start gap-3"
                        style={{ background: "rgba(250,204,21,0.07)", border: "1px solid rgba(250,204,21,0.2)" }}>
                        <Lightbulb size={15} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs font-bold text-yellow-400 uppercase tracking-widest mb-1">Top Priority</div>
                          <p className="text-sm text-slate-200">{aiResult.data.top_tip}</p>
                        </div>
                      </div>
                    )}

                    {/* Field-by-field */}
                    {[
                      { label: "Headline", note: aiResult.data.headline_note, current: aiPanel.banner?.title, suggestion: aiResult.data.improved_title, field: "title" },
                      { label: "Tagline", note: aiResult.data.tagline_note, current: aiPanel.banner?.tagline, suggestion: aiResult.data.improved_tagline, field: "tagline" },
                      { label: "CTA Button", note: aiResult.data.cta_note, current: aiPanel.banner?.cta_text, suggestion: aiResult.data.improved_cta_text, field: "cta_text" },
                    ].map(({ label, note, current, suggestion, field }) => (
                      <div key={field} className="rounded-xl p-4 space-y-2"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">{label}</div>
                        {note && <p className="text-sm text-slate-300">{note}</p>}
                        {suggestion && suggestion !== current && (
                          <div className="flex items-center gap-3 pt-1">
                            <div className="flex-1">
                              <div className="text-xs text-slate-500 mb-0.5">Suggested</div>
                              <div className="text-[#2ECC71] font-semibold text-sm">{suggestion}</div>
                            </div>
                            <button
                              onClick={() => { applyAISuggestion(field, suggestion); if (!showBannerForm) { openEditBanner(aiPanel.banner); } }}
                              className="flex-shrink-0 flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg"
                              style={{ background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "1px solid rgba(46,204,113,0.3)" }}
                              data-testid={`apply-improve-${field}`}>
                              <Plus size={10} /> Apply
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* My Jobs Tab */}
        {!loading && tab === "jobs" && (
          <div data-testid="jobs-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">My Job Postings</h2>
                <p className="text-slate-500 text-sm">$50/post · 90-day listing · auto-deleted after expiry</p>
              </div>
              <Link to="/post-a-job" className="btn-action !py-2 !px-4 !text-sm flex items-center gap-1.5" data-testid="post-new-job-btn">
                <Briefcase size={14} /> Post a Job
              </Link>
            </div>
            {myJobs.length === 0 ? (
              <div className="card-base text-center py-10">
                <Briefcase size={40} className="text-[#003366] mx-auto mb-3" />
                <p className="font-semibold text-[#0F172A] mb-1">No job postings yet</p>
                <p className="text-slate-400 text-sm mb-4">Post a job to attract local talent — $50 for 90 days</p>
                <Link to="/post-a-job" className="btn-action inline-block" data-testid="first-job-btn">Post Your First Job</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {myJobs.map(job => {
                  const daysLeft = job.expires_at
                    ? Math.max(0, Math.ceil((new Date(job.expires_at) - new Date()) / 86400000))
                    : null;
                  const isExpired = job.payment_status !== "active" || (daysLeft !== null && daysLeft <= 0);
                  return (
                    <div key={job.id} className="card-base" data-testid={`my-job-${job.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-[#0F172A]">{job.title}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${isExpired ? "bg-slate-100 text-slate-500" : "bg-[#2ECC71]/10 text-[#27AE60]"}`}>
                              {isExpired ? "Expired" : job.payment_status === "active" ? "Active" : "Pending Payment"}
                            </span>
                          </div>
                          <p className="text-slate-500 text-sm">{job.company_name}</p>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            {job.locations?.slice(0, 2).map((l, i) => (
                              <span key={i} className="text-xs text-slate-400 flex items-center gap-1">
                                <MapPin size={10} /> {l.city ? `${l.city}, ${l.state}` : l.zip_code}
                              </span>
                            ))}
                            {daysLeft !== null && !isExpired && (
                              <span className={`text-xs font-semibold flex items-center gap-1 ${daysLeft <= 7 ? "text-red-400" : "text-slate-400"}`}>
                                <Clock size={10} /> {daysLeft}d remaining
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {job.payment_status === "active" && (
                            <Link to={`/jobs/${job.id}`}
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors"
                              style={{ border: "1px solid #e2e8f0", color: "#003366" }}>
                              <ExternalLink size={11} /> View
                            </Link>
                          )}
                          {isExpired && (
                            <Link to="/post-a-job"
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-all"
                              style={{ background: "#003366" }}>
                              Repost — $50
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Referral Tab */}
        {!loading && tab === "referral" && (
          <div data-testid="referral-tab">
            <h2 className="text-lg font-bold text-[#0F172A] mb-1">Refer & Earn</h2>
            <p className="text-[#64748B] text-sm mb-6">Share your link. When a colleague claims their first ZIP, you both get <strong className="text-[#2ECC71]">$25 credit</strong> automatically applied to your next checkout.</p>

            {/* Credit Balance Card */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#003366] rounded-xl p-5 text-white" data-testid="credit-balance-card">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign size={16} className="text-[#2ECC71]" />
                  <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Credit Balance</span>
                </div>
                <div className="text-3xl font-black text-[#2ECC71]">${(referralStats?.credit_balance || 0).toFixed(0)}</div>
                <div className="text-xs text-slate-400 mt-1">Auto-applied at checkout</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="paid-referrals-card">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={16} className="text-[#003366]" />
                  <span className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Paid Referrals</span>
                </div>
                <div className="text-3xl font-black text-[#003366]">{referralStats?.paid_referrals || 0}</div>
                <div className="text-xs text-[#64748B] mt-1">Colleagues who paid after your link</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Gift size={16} className="text-[#003366]" />
                  <span className="text-xs font-semibold text-[#64748B] uppercase tracking-wide">Total Earned</span>
                </div>
                <div className="text-3xl font-black text-[#003366]">${((referralStats?.paid_referrals || 0) * 25).toFixed(0)}</div>
                <div className="text-xs text-[#64748B] mt-1">Lifetime referral credits</div>
              </div>
            </div>

            {/* Referral Link */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="referral-link-section">
              <h3 className="font-bold text-[#0F172A] mb-3">Your Referral Link</h3>
              <div className="flex gap-2 items-center">
                <div className="flex-1 bg-[#F8FAFC] border border-slate-200 rounded-lg px-4 py-3 text-sm font-mono text-[#334155] overflow-x-auto whitespace-nowrap" data-testid="referral-link-display">
                  {referralStats?.referral_link || `https://nextdayrefund.com/claim?ref=${referralStats?.referral_code || "..."}`}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(referralStats?.referral_link || "");
                    setRefLinkCopied(true);
                    setTimeout(() => setRefLinkCopied(false), 2000);
                  }}
                  className="flex-shrink-0 flex items-center gap-1.5 bg-[#003366] text-white font-semibold px-4 py-3 rounded-lg hover:bg-[#004080] transition-colors text-sm"
                  data-testid="copy-referral-link-btn"
                >
                  {refLinkCopied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <p className="text-xs text-[#64748B] mt-3">Share this with fellow tax pros, insurance agents, or any local service business. They sign up → claim a ZIP → you both get $25 off your next order.</p>
            </div>

            {/* How it works */}
            <div className="bg-[#F0FDF4] border border-[#2ECC71]/30 rounded-xl p-5">
              <h3 className="font-bold text-[#0F172A] mb-3">How it works</h3>
              <div className="space-y-3">
                {[
                  { step: "1", text: "Copy your referral link and share it via email, text, or social media" },
                  { step: "2", text: "Your contact visits the link, registers, and claims a ZIP code" },
                  { step: "3", text: "Once their payment clears, $25 is added to both of your accounts automatically" },
                  { step: "4", text: "Your credit is applied on your next ZIP claim checkout — no coupon codes needed" },
                ].map(({ step, text }) => (
                  <div key={step} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#003366] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{step}</div>
                    <p className="text-sm text-[#334155]">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Badge Tab */}
        {!loading && tab === "badge" && (
          <div data-testid="badge-tab">
            <h2 className="text-lg font-bold text-[#0F172A] mb-2">Your Verified Provider Badge</h2>
            <p className="text-[#64748B] text-sm mb-6">Copy and paste this HTML snippet onto your website to display your official verification badge.</p>
            <div className="flex justify-center mb-6">
              <div style={{background:"#003366",color:"#fff",borderRadius:"8px",padding:"12px 18px",display:"inline-flex",alignItems:"center",gap:"10px",fontFamily:"Arial,sans-serif",boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
                <span style={{background:"#2ECC71",color:"#fff",borderRadius:"50%",width:"24px",height:"24px",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:"bold",fontSize:"14px",flexShrink:0}}>✓</span>
                <div>
                  <div style={{fontSize:"10px",textTransform:"uppercase",letterSpacing:"1px",opacity:0.7,marginBottom:"2px"}}>Verified Provider</div>
                  <div style={{fontWeight:"bold",fontSize:"13px"}}>NextDayRefund.com</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <pre className="bg-[#F8FAFC] border border-slate-200 rounded-lg p-4 text-xs text-[#334155] overflow-x-auto whitespace-pre-wrap">{badgeHtml}</pre>
              <button onClick={copyBadge} className="absolute top-3 right-3 flex items-center gap-1.5 bg-white border border-slate-200 rounded px-3 py-1.5 text-xs font-semibold text-[#003366] hover:bg-[#003366] hover:text-white transition-all" data-testid="portal-copy-badge-btn">
                {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy Code</>}
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
